// /cms-frontend/src/services/api.ts

import axios from "axios";

const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:5000"; // Use environment variable with fallback for development

// Set up axios instance
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptors for request and response
api.interceptors.request.use(
  (config) => {
    // Add authorization token if available
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Skip ngrok browser warning page
    config.headers["ngrok-skip-browser-warning"] = "true";

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    return response.data; // Extract response.data automatically
  },
  (error) => {
    // Handle errors globally and extract meaningful error messages
    console.error("API Error:", error);

    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      console.warn("401 Unauthorized - Token may be expired or invalid");

      // Check if the error message indicates token expiration
      const errorMessage = error.response?.data?.message || "";
      if (
        errorMessage.includes("expired") ||
        errorMessage.includes("invalid") ||
        errorMessage.includes("token failed")
      ) {
        // Clear the token and redirect to login only for token-related issues
        localStorage.removeItem("token");

        // Only redirect if we're not already on the login page
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(
          new Error("Session expired. Please log in again.")
        );
      }

      // For other 401 errors (like insufficient permissions), don't redirect
      return Promise.reject(new Error(errorMessage || "Unauthorized access"));
    }

    // If there's a response, extract the error message
    if (error.response?.data) {
      const errorData = error.response.data;

      let errorMessage = errorData.message || "An error occurred";

      // Handle validation errors (array format)
      if (errorData.errors && Array.isArray(errorData.errors)) {
        errorMessage = errorData.errors.map((err: any) => err.msg).join(", ");
      }

      // Create a meaningful error object
      const enhancedError: any = new Error(errorMessage);
      enhancedError.response = error.response;
      enhancedError.data = errorData;
      enhancedError.status = error.response.status;

      return Promise.reject(enhancedError);
    }

    return Promise.reject(error);
  }
);

// Example API calls
export const getStudents = async () => {
  return await api.get("/students");
};

export const getTeachers = async () => {
  return await api.get("/teachers");
};

export const getFees = async () => {
  return await api.get("/fees");
};

export const getBranches = async () => {
  return await api.get("/branches");
};

// Add more API functions as needed
export default api;
